import React, { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Trash2, Plus, Save, Loader2 } from "lucide-react";
import api from "../api";

const inputCls = "bg-transparent border border-white/15 px-3 py-2 text-sm text-white focus:border-[#00C3FF] outline-none";

function TargetRow({ t, i, count, name, onMove, onRemove, onReason }) {
  return (
    <div data-testid={`synergy-target-${t.slug}`} className="bg-[#0C0C12] p-3 flex flex-wrap items-center gap-3">
      <span className="font-mono-tech text-xs text-[#5C657A] w-5">{i + 1}.</span>
      <span className="text-sm text-white w-48 truncate">{name}</span>
      <input data-testid={`synergy-reason-${t.slug}`} value={t.reason} onChange={(e) => onReason(e.target.value)} placeholder="Short research note" maxLength={80} className={`${inputCls} flex-1 min-w-[200px]`} />
      <button data-testid={`synergy-up-${t.slug}`} disabled={i === 0} onClick={() => onMove(-1)} className="p-2 text-[#8A94A6] hover:text-white disabled:opacity-30"><ArrowUp size={14} /></button>
      <button data-testid={`synergy-down-${t.slug}`} disabled={i === count - 1} onClick={() => onMove(1)} className="p-2 text-[#8A94A6] hover:text-white disabled:opacity-30"><ArrowDown size={14} /></button>
      <button data-testid={`synergy-remove-${t.slug}`} onClick={onRemove} className="p-2 text-[#5C657A] hover:text-[#FF4D6D]"><Trash2 size={14} /></button>
    </div>
  );
}

export function SynergyEditor({ products }) {
  const [map, setMap] = useState({});
  const [slug, setSlug] = useState("");
  const [targets, setTargets] = useState([]);
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const names = Object.fromEntries(products.map((p) => [p.slug, p.name]));

  useEffect(() => { api.get("/admin/synergies").then(({ data }) => setMap(data)); }, []);
  useEffect(() => { setTargets(map[slug] || []); setMsg(""); }, [slug, map]);

  const move = (i, d) => { const a = [...targets]; [a[i], a[i + d]] = [a[i + d], a[i]]; setTargets(a); };
  const add = () => { if (adding) { setTargets([...targets, { slug: adding, reason: "" }]); setAdding(""); } };
  const save = async () => {
    setBusy(true); setMsg("");
    try {
      const { data } = await api.put(`/admin/synergies/${slug}`, { targets });
      setMap({ ...map, [slug]: data.targets }); setMsg("Saved — live on the product page and cart now.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      setMsg(Array.isArray(d) ? d.map((x) => x.msg).join("; ") : d || "Save failed.");
    } finally { setBusy(false); }
  };
  const taken = new Set([slug, ...targets.map((t) => t.slug)]);

  return (
    <div data-testid="synergy-editor">
      <div className="overline mb-2">COMPLEMENTARY COMPOUNDS</div>
      <p className="text-sm text-[#8A94A6] mb-4">Pick a product, then add, remove or reorder what it recommends (up to 6). Order here = order shown on the product page and in the cart.</p>
      <select data-testid="synergy-product-select" value={slug} onChange={(e) => setSlug(e.target.value)} className={`${inputCls} bg-[#0C0C12] min-w-[260px] mb-4`}>
        <option value="">Select a product…</option>
        {products.map((p) => <option key={p.slug} value={p.slug}>{p.name} ({(map[p.slug] || []).length})</option>)}
      </select>
      {slug && (
        <>
          <div className="space-y-px bg-white/5">
            {targets.length === 0 && <div data-testid="synergy-empty" className="bg-[#0C0C12] p-4 text-xs text-[#5C657A]">No recommendations yet.</div>}
            {targets.map((t, i) => (
              <TargetRow key={t.slug} t={t} i={i} count={targets.length} name={names[t.slug] || t.slug}
                onMove={(d) => move(i, d)} onRemove={() => setTargets(targets.filter((_, j) => j !== i))}
                onReason={(v) => setTargets(targets.map((x, j) => (j === i ? { ...x, reason: v } : x)))} />
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-4">
            {targets.length < 6 && (
              <>
                <select data-testid="synergy-add-select" value={adding} onChange={(e) => setAdding(e.target.value)} className={`${inputCls} bg-[#0C0C12] min-w-[220px]`}>
                  <option value="">Add a product…</option>
                  {products.filter((p) => !taken.has(p.slug)).map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                </select>
                <button data-testid="synergy-add-btn" onClick={add} disabled={!adding} className="inline-flex items-center gap-1 px-3 py-2 border border-white/15 text-xs text-[#8A94A6] hover:text-white disabled:opacity-40"><Plus size={12} /> Add</button>
              </>
            )}
            <button data-testid="synergy-save-btn" onClick={save} disabled={busy} className="btn-primary text-[10px]" style={{ padding: "10px 16px" }}>
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
            </button>
            {msg && <span data-testid="synergy-msg" className="text-xs text-[#8A94A6]">{msg}</span>}
          </div>
        </>
      )}
    </div>
  );
}
